// Result-video blobs waiting to upload. localStorage holds the outbox record but is
// far too small for a three-minute video, so the bytes live in IndexedDB keyed by
// the record's local id. A copy of cancellation-media.ts with its own database, so the two
// outboxes never share (or clear) each other's blobs.
//
// When IndexedDB is unavailable (private mode on some browsers, jsdom) blobs fall back to an
// in-memory map: they survive for the session, and a record that outlives the page finds its
// blob missing — the status card then asks the agent to record again.

const DB_NAME = 'ielect-result-video';
const STORE = 'media';

const memory = new Map<string, Blob>();
let dbPromise: Promise<IDBDatabase | null> | null = null;

const openDb = (): Promise<IDBDatabase | null> => {
	if (dbPromise) return dbPromise;
	dbPromise = new Promise((resolve) => {
		try {
			if (typeof indexedDB === 'undefined') {
				resolve(null);
				return;
			}
			const req = indexedDB.open(DB_NAME, 1);
			req.onupgradeneeded = () => {
				if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
			};
			req.onsuccess = () => resolve(req.result);
			req.onerror = () => resolve(null);
			req.onblocked = () => resolve(null);
		} catch {
			resolve(null);
		}
	});
	return dbPromise;
};

const run = async <T>(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest): Promise<T | undefined> => {
	const db = await openDb();
	if (!db) return undefined;
	return new Promise((resolve) => {
		try {
			const req = op(db.transaction(STORE, mode).objectStore(STORE));
			req.onsuccess = () => resolve(req.result as T);
			req.onerror = () => resolve(undefined);
		} catch {
			resolve(undefined);
		}
	});
};

/** True when blobs will survive a reload. */
export const durableVideoStorageAvailable = async (): Promise<boolean> => !!(await openDb());

export const putVideoBlob = async (id: string, blob: Blob): Promise<void> => {
	memory.set(id, blob);
	await run('readwrite', (s) => s.put(blob, id));
};

export const getVideoBlob = async (id: string): Promise<Blob | null> => {
	const hit = memory.get(id);
	if (hit) return hit;
	const stored = await run<Blob>('readonly', (s) => s.get(id));
	return stored instanceof Blob ? stored : null;
};

export const deleteVideoBlob = async (id: string): Promise<void> => {
	memory.delete(id);
	await run('readwrite', (s) => s.delete(id));
};
